// t() reads the page language, which the compiler cannot see.
"use no memo";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import CheckCircle from "~icons/ph/check-circle";
import Devices from "~icons/ph/devices";
import DeviceMobile from "~icons/ph/device-mobile";
import ShieldWarning from "~icons/ph/shield-warning";
import WarningCircle from "~icons/ph/warning-circle";
import { useAppLocation } from "../app/location";
import { useShell } from "../app/shell";
import { ApiError } from "../data/api";
import { useApproveDevice, useSubmitting } from "../data/mutations";
import { deviceSignInOptions } from "../data/queries";
import { useAccount } from "../data/session";
import { t, time, useLocale } from "../i18n";

function Card({
  icon,
  title,
  lead,
  children,
}: {
  icon: ReactNode;
  title: string;
  lead?: string;
  children: ReactNode;
}) {
  return (
    <div className="call-invite">
      <span className="call-invite-icon" aria-hidden="true">
        {icon}
      </span>
      <h1 id="approve-title">{title}</h1>
      {lead && <p className="call-invite-lead">{lead}</p>}
      {children}
    </div>
  );
}

function SignInFirst() {
  const { open, openScreen } = useShell();
  const signIn = () => open({ kind: "enter", step: "restore" });
  useEffect(signIn, [open]);
  return (
    <Card
      icon={<DeviceMobile width={28} height={28} />}
      title={t("سجّل الدخول للموافقة")}
      lead={t("سجّل الدخول على هذا الجهاز أولًا، ثم وافق على الجهاز الجديد.")}
    >
      <div className="call-invite-actions">
        <button className="button" onClick={signIn}>
          {t("تسجيل الدخول")}
        </button>
        <button className="button secondary" onClick={() => openScreen("home")}>
          {t("العودة إلى الرئيسية")}
        </button>
      </div>
    </Card>
  );
}

function Review({ accountId, code }: { accountId: string; code: string }) {
  const { openScreen } = useShell();
  const approve = useApproveDevice();
  const submitting = useSubmitting();
  const [approved, setApproved] = useState(false),
    [refused, setRefused] = useState<Error | null>(null);
  const request = useQuery({
    ...deviceSignInOptions(accountId, code),
    enabled: !approved && !refused,
  });
  const goHome = () => openScreen("home");
  async function confirm() {
    try {
      await approve.mutateAsync(code);
      setApproved(true);
    } catch (e) {
      setRefused(e as Error);
    } finally {
      approve.reset();
    }
  }
  if (approved)
    return (
      <Card
        icon={<CheckCircle width={28} height={28} />}
        title={t("تمت الموافقة")}
        lead={t("سيدخل الجهاز إلى حسابك خلال لحظات.")}
      >
        <button className="button secondary" onClick={goHome}>
          {t("العودة إلى الرئيسية")}
        </button>
      </Card>
    );
  const problem = refused ?? request.error;
  if (problem)
    return (
      <Card
        icon={<WarningCircle width={28} height={28} />}
        title={t("تعذرت الموافقة")}
        lead={t(problem.message)}
      >
        <div className="call-invite-actions">
          {/* Only a limit or a lost connection can pass with another try. */}
          {problem instanceof ApiError && problem.status !== 400 && (
            <button
              className="button"
              onClick={() => {
                setRefused(null);
                void request.refetch();
              }}
            >
              {t("إعادة المحاولة")}
            </button>
          )}
          <button className="button secondary" onClick={goHome}>
            {t("العودة إلى الرئيسية")}
          </button>
        </div>
      </Card>
    );
  if (!request.data)
    return (
      <p className="call-invite-lead" role="status">
        {t("جارٍ التحقق من الرمز…")}
      </p>
    );
  const { device, place, created_at } = request.data;
  return (
    <Card
      icon={<Devices width={28} height={28} />}
      title={t("هل تريد إدخال هذا الجهاز إلى حسابك؟")}
    >
      <dl className="device-request">
        <div>
          <dt>{t("الجهاز")}</dt>
          <dd dir="ltr">{device}</dd>
        </div>
        {place && (
          <div>
            <dt>{t("المكان")}</dt>
            <dd dir="ltr">{place}</dd>
          </div>
        )}
        <div>
          <dt>{t("الوقت")}</dt>
          <dd>{time(created_at)}</dd>
        </div>
      </dl>
      <p className="device-warning">
        <ShieldWarning width={20} height={20} aria-hidden="true" />
        {t(
          "من يملك هذا الرمز يستطيع دخول حسابك. لا توافق إلا إذا كان هذا الجهاز أمامك.",
        )}
      </p>
      <div className="call-invite-actions">
        <button
          className="button"
          disabled={submitting}
          onClick={() => void confirm()}
        >
          {t("موافقة")}
        </button>
        <button className="button secondary" onClick={goHome}>
          {t("ليس أنا")}
        </button>
      </div>
    </Card>
  );
}

// Signed out, the person signs in over this page and stays on it.
export function ApproveScreen() {
  useLocale();
  const { signInCode } = useAppLocation();
  const account = useAccount();
  return (
    <main id="main-content" tabIndex={-1} className="search-home">
      <section
        className="search-hero invite-hero"
        aria-labelledby="approve-title"
      >
        {account ? (
          <Review accountId={account.me.id} code={signInCode ?? ""} />
        ) : (
          <SignInFirst />
        )}
      </section>
    </main>
  );
}
